// Query key invalidation occurs in src/routes/_auth/organization/groups/$groupId.tsx

import { queryOptions } from "@tanstack/react-query";
import { httpPostGraphql } from "shared/api/httpClient";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { useUserId } from "shared/stores/oidc";
import { to } from "shared/utils/fns";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";
import { GroupLikeFragment, SenderLikeFragment } from "src/gql/graphql";

/** @public */
export function useCreateGroup() {
    const { mutate: createGroup, mutateAsync: createGroupAsync } = useGraphqlMutation({
        document: mutations.createGroup,
        onSuccess: async () => {
            await Promise.all([qk.invalidate("user"), qk.invalidate("group")]);
            notify.show.success({ message: "Created new group" });
        },
    });
    return { createGroup, createGroupAsync };
}

// for the following three mutations
/** @public */
export function useUpdateGroup() {
    const { mutateAsync: updateGroupAsync } = useGraphqlMutation(mutations.updateGroup);
    return { updateGroupAsync };
}

/** @public */
export function useAddParentToGroup(onError?: (parentId: string) => void) {
    const { mutateAsync: addParentAsync } = useGraphqlMutation({
        document: mutations.addParent,
        onError: (error, vars) => onError?.(vars.parentId),
    });
    return { addParentAsync };
}

/** @public */
export function useRemoveParentFromGroup() {
    const { mutateAsync: removeParentAsync } = useGraphqlMutation(mutations.removeParent);
    return { removeParentAsync };
}

/** @public */
export function useAddUserToGroup() {
    const { userId } = useUserId();
    const { mutate: addUser, mutateAsync: addUserAsync } = useGraphqlMutation({
        document: mutations.addUserToGroup,
        onSuccess: async (data) => {
            if (!!data?.addUserToGroup) {
                const group = data.addUserToGroup as GroupLikeFragment;
                await Promise.all([qk.invalidate("user", "id", userId), qk.invalidate("group", "id", group.id, "directMembers")]);
            }
        },
    });
    return { addUser, addUserAsync };
}

const mutations = {
    createGroup: graphql(`
        mutation CreateGroup($internalJoinable: Boolean!, $name: String!, $parentGroupIds: [UUID!]!, $members: [UUID!]!) {
            createGroup(params: { internalJoinable: $internalJoinable, name: $name, parentGroupIds: $parentGroupIds, members: $members }) {
                ...FullGroupLike
            }
        }
    `),
    removeMembersFromGroup: graphql(`
        mutation RemoveGroupMember($userId: UUID!, $groupId: UUID!) {
            removeUserFromGroup(userId: $userId, groupId: $groupId) {
                id
            }
        }
    `),
    updateGroup: graphql(`
        mutation UpdateGroup($groupId: UUID!, $internalJoinable: Boolean!, $name: String!) {
            updateGroup(id: $groupId, params: { internalJoinable: $internalJoinable, name: $name }) {
                id
            }
        }
    `),
    addParent: graphql(`
        mutation AddParentGrouop($groupId: UUID!, $parentId: UUID!) {
            addParentToGroup(groupId: $groupId, parentId: $parentId) {
                id
            }
        }
    `),
    removeParent: graphql(`
        mutation RemoveParentGroup($groupId: UUID!, $parentId: UUID!) {
            removeParentFromGroup(groupId: $groupId, parentId: $parentId) {
                id
            }
        }
    `),
    addUserToGroup: graphql(`
        mutation AddUserToGroup($groupId: UUID!, $userId: UUID!) {
            addUserToGroup(groupId: $groupId, userId: $userId) {
                ...GroupLike
            }
        }
    `),
};

/** @public */
export const groupsQueryOptions = {
    rootGroup: queryOptions({
        queryKey: qk("group", "rootGroup"),
        queryFn: () => httpPostGraphql(groupsGql.rootGroupQuery, {}),
        select: ({ rootGroup }) => ({ rootGroup: rootGroup satisfies GroupLikeFragment }),
    }),
    group: (id: string) =>
        queryOptions({
            queryKey: qk("group", "id", id, "base"),
            queryFn: () => httpPostGraphql(groupsGql.groupQuery, { id }),
            select: ({ group }) => ({ group: group satisfies GroupLikeFragment }),
        }),
    groupDetails: (id: string) =>
        queryOptions({
            queryKey: qk("group", "id", id, "details"),
            queryFn: () => httpPostGraphql(groupsGql.groupDetailsQuery, { id }),
            select: ({ group }) => ({ group: group satisfies GroupLikeFragment }),
        }),
    groupSenders: (id: string) =>
        queryOptions({
            queryKey: qk("group", "id", id, "senders"),
            queryFn: () => httpPostGraphql(groupsGql.groupSendersQuery, { id }),
            select: (data) => ({ senders: data.group.senders satisfies SenderLikeFragment[] }),
        }),
    allMembers: (id: string, offset: number, limit: number) =>
        queryOptions({
            queryKey: qk("group", "id", id, "allMembers"),
            queryFn: () => httpPostGraphql(groupsGql.allMembersQuery, { id, offset, limit }),
        }),
    directMembersCount: (id: string) =>
        queryOptions({
            queryKey: qk("group", "id", id, "directMembersCount"),
            queryFn: () => httpPostGraphql(groupsGql.directMembersCount, { id }),
            select: (data) => data.group.aboutMembership.directMembersCount,
        }),
    search: (search: string) =>
        queryOptions({
            queryKey: qk("messages", "composer", "groups", "search", search),
            queryFn: () => httpPostGraphql(groupsGql.groupSearchQuery, { search }),
            select: to("searchGroups"),
        }),
};

const groupsGql = {
    rootGroupQuery: graphql(`
        query RootGroup {
            rootGroup {
                ...GroupLike
            }
        }
    `),
    groupQuery: graphql(`
        query Group($id: UUID!) {
            group(id: $id) {
                ...GroupLike
            }
        }
    `),
    groupDetailsQuery: graphql(`
        query GroupDetails($id: UUID!) {
            group(id: $id) {
                ...FullGroupLike
            }
        }
    `),
    parentQuery: graphql(`
        query GroupParent($groupId: UUID!, $offset: Int!, $limit: Int!) {
            group(id: $groupId) {
                parentsPaged(offset: $offset, limit: $limit) {
                    ...GroupLike
                }
            }
        }
    `),
    groupSendersQuery: graphql(`
        query GroupSenders($id: UUID!) {
            group(id: $id) {
                senders {
                    ...SenderLike
                }
            }
        }
    `),
    allMembersQuery: graphql(`
        query AllMembers($id: UUID!, $offset: Int!, $limit: Int!) {
            group(id: $id) {
                aboutMembership {
                    allMembers(offset: $offset, limit: $limit) {
                        ...MemberLike
                    }
                }
            }
        }
    `),
    directMembersQuery: graphql(`
        query DirectMembers($id: UUID!, $offset: Int!, $limit: Int!) {
            group(id: $id) {
                aboutMembership {
                    directMembers(offset: $offset, limit: $limit) {
                        ...MemberLike
                    }
                }
            }
        }
    `),
    directMembersCount: graphql(`
        query DirectMembersCount($id: UUID!) {
            group(id: $id) {
                aboutMembership {
                    directMembersCount
                }
            }
        }
    `),
    groupSearchQuery: graphql(`
        query GroupSearch($search: String!) {
            searchGroups(limit: 10, offset: 0, permissions: [], query: $search) {
                ...FullGroupLike
                aboutMembership {
                    allMembersCount
                }
            }
        }
    `),
};
